/**
 * The portal's small top navigation: Home, Invoices, Approvals, Documents.
 * Four equal tabs (icon over label) on a phone, pills from `sm` up.
 */

import { ClipboardCheck, FolderOpen, Home, Receipt, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface PortalNavItem {
  to: string;
  key: 'home' | 'invoices' | 'approvals' | 'documents';
  icon: LucideIcon;
  /** Active only on the exact path (home), not on its children. */
  end?: boolean;
}

export const PORTAL_NAV: readonly PortalNavItem[] = [
  { to: '/portal', key: 'home', icon: Home, end: true },
  { to: '/portal/invoices', key: 'invoices', icon: Receipt },
  { to: '/portal/approvals', key: 'approvals', icon: ClipboardCheck },
  { to: '/portal/documents', key: 'documents', icon: FolderOpen },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex min-w-0 flex-col items-center gap-1 rounded-xl px-1 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-row sm:gap-2 sm:rounded-full sm:px-3 sm:text-sm',
    isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
  );

export function PortalNav() {
  const { t } = useTranslation('portal');
  return (
    <nav aria-label={t('nav.label')}>
      <ul className="grid grid-cols-4 gap-1 py-2 sm:flex sm:gap-1.5">
        {PORTAL_NAV.map(({ to, key, icon: Icon, end }) => (
          <li key={key}>
            <NavLink to={to} end={end} className={linkClass}>
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="max-w-full truncate">{t(`nav.${key}`)}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
